import { useMemo, useCallback } from 'react';
import I18n from 'I18n';
import Flow from 'automation-ui-canvas/flow/records/Flow';
import useSettingsModelState from 'automation-ui-canvas/containers/settings/v2/useSettingsModelState';
import { isFilterEmpty } from 'automation-ui-flow-core/enrollment/enrollmentUtils';
import { getAssociatedLists, getClassicEnrollmentSettings, getGoalFilters, getSuppressionFilters, getSuppressionLists } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { getEnrollmentList } from 'automation-ui-enrollment/utils/listFilters';
import UnenrollmentSettingsSection from 'automation-ui-canvas/containers/settings/v2/UnenrollmentSettingsSection';
import { jsx as _jsx } from "react/jsx-runtime";
export default function EnrollmentSettingsStep({
  flow,
  setFlow,
  selectedTriggerOption
}) {
  var _getEnrollmentList;
  const flowRecord = useMemo(() => new Flow(Object.assign({}, flow, {
    flowId: NaN
  })), [flow]);
  const classicEnrollmentSettings = useSettingsModelState({
    flow: flowRecord,
    initialize: initialFlow => {
      return getClassicEnrollmentSettings(initialFlow);
    },
    useStaged: false
  });

  /* platform suppression filters */
  const suppressionFilters = useSettingsModelState({
    flow: flowRecord,
    initialize: initialFlow => {
      if (initialFlow.isClassicWorkflow) {
        return {
          value: null
        };
      }
      return {
        value: getSuppressionFilters(initialFlow)
      };
    },
    useStaged: false
  });

  /* classic suppression lists */
  const suppressionLists = useSettingsModelState({
    flow: flowRecord,
    initialize: initialFlow => {
      return {
        value: getSuppressionLists(initialFlow)
      };
    },
    useStaged: false
  });
  const goalFilters = useSettingsModelState({
    flow: flowRecord,
    initialize: initialFlow => {
      /* initialize to null for platform workflows */
      if (!initialFlow.isClassicWorkflow) {
        return {
          value: null
        };
      }
      return {
        value: getGoalFilters(initialFlow)
      };
    },
    useStaged: false
  });
  const enrollmentList = useSettingsModelState({
    flow: flowRecord,
    initialize: initialFlow => {
      /* initialize to null for platform workflows */
      return {
        value: getEnrollmentList(initialFlow.toHybridFlowInterface())
      };
    },
    useStaged: false
  });
  const handleChange = useCallback(({
    suppressionFilters: updatedSuppressionFilters,
    suppressionLists: updatedSuppressionLists,
    classicEnrollmentSettings: updatedClassicEnrollmentSettings,
    goalFilters: updatedGoalFilters,
    enrollmentList: updatedEnrollmentList
  }) => {
    setFlow(draftFlow => {
      if (draftFlow.classicEnrollmentSettings && updatedClassicEnrollmentSettings) {
        var _updatedClassicEnroll, _updatedClassicEnroll2;
        draftFlow.classicEnrollmentSettings.listening = updatedClassicEnrollmentSettings.listening;
        draftFlow.classicEnrollmentSettings.goalCriteria = updatedClassicEnrollmentSettings.goalCriteria.toJS();
        draftFlow.classicEnrollmentSettings.goalFilters = null;
        draftFlow.classicEnrollmentSettings.suppressionListIds = updatedClassicEnrollmentSettings.suppressionListIds.toJS();
        draftFlow.classicEnrollmentSettings.allowEnrollmentFromMerge = (_updatedClassicEnroll = updatedClassicEnrollmentSettings.allowEnrollmentFromMerge) !== null && _updatedClassicEnroll !== void 0 ? _updatedClassicEnroll : draftFlow.classicEnrollmentSettings.allowEnrollmentFromMerge;
        draftFlow.classicEnrollmentSettings.canEnrollFromSalesforce = (_updatedClassicEnroll2 = updatedClassicEnrollmentSettings.canEnrollFromSalesforce) !== null && _updatedClassicEnroll2 !== void 0 ? _updatedClassicEnroll2 : draftFlow.classicEnrollmentSettings.canEnrollFromSalesforce;
      }
      let associatedLists = getAssociatedLists(flowRecord).toJS();
      if (updatedGoalFilters) {
        associatedLists = associatedLists.map(list => Object.assign({}, list, {
          listTypes: list.listTypes.filter(listType => listType !== 'CLASSIC_GOAL_LIST')
        }));
        if (!isFilterEmpty(updatedGoalFilters.value)) {
          associatedLists.push({
            listTypes: ['CLASSIC_GOAL_LIST'],
            filterBranch: updatedGoalFilters.value
          });
        }
      }
      if (updatedSuppressionLists) {
        associatedLists = associatedLists.map(list => Object.assign({}, list, {
          listTypes: list.listTypes.filter(listType => listType !== 'SUPPRESSION_LIST')
        }));
        if (updatedSuppressionLists.value) {
          associatedLists = associatedLists.concat(updatedSuppressionLists.value.toJS());
        }
      }
      if (updatedSuppressionFilters) {
        associatedLists = associatedLists.map(list => Object.assign({}, list, {
          listTypes: list.listTypes.filter(listType => listType !== 'SUPPRESSION_LIST')
        }));
        if (!isFilterEmpty(updatedSuppressionFilters.value)) {
          associatedLists.push({
            listTypes: ['SUPPRESSION_LIST'],
            filterBranch: updatedSuppressionFilters.value
          });
        }
      }
      if (updatedEnrollmentList) {
        associatedLists = associatedLists.filter(list => !list.listTypes.includes('ENROLLMENT_LIST'));
        associatedLists.push(updatedEnrollmentList.value);
      }
      associatedLists = associatedLists.filter(list => list.listTypes.length > 0);
      draftFlow.associatedLists = associatedLists;
    });
  }, [setFlow, flowRecord]);
  return /*#__PURE__*/_jsx(UnenrollmentSettingsSection, {
    flow: flowRecord,
    readOnly: false,
    suppressionFilters: suppressionFilters,
    goalFilters: goalFilters,
    classicEnrollmentSettings: classicEnrollmentSettings,
    suppressionLists: suppressionLists,
    enrollmentList: enrollmentList,
    isListeningSettingDisabled: {
      value: !(selectedTriggerOption.type === 'LIST' && !isFilterEmpty((_getEnrollmentList = getEnrollmentList(flowRecord.toHybridFlowInterface())) === null || _getEnrollmentList === void 0 ? void 0 : _getEnrollmentList.filterBranch))
    },
    onChange: handleChange
  });
}
export const enrollmentSettingsStep = {
  component: EnrollmentSettingsStep,
  getLabel: () => I18n.text('createWorkflow.step.enrollmentSettings')
};